import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { GaRouteTracker } from "@/components/GaRouteTracker";
import { useTheme } from "@/hooks/useTheme";
import { createContext, Suspense } from "react";
import { lazyWithRetry as lazy } from "./lib/lazyWithRetry";
import { RouteErrorBoundary, LoadingSpinner } from "./components/RouteErrorBoundary";
import Index from "./pages/Index";
import PartnerGuard from "@/components/partners/PartnerGuard";
import { categoryFromSlug } from "@/data/blog-posts";
import NotFound from "./pages/NotFound";
const SecureFileAgentProduct = lazy(() => import("./pages/productos/SecureFileAgentProduct"));
const SecureFileMftProduct = lazy(() => import("./pages/productos/SecureFileMftProduct"));
const WsGuardianProduct = lazy(() => import("./pages/productos/WsGuardianProduct"));
const SecureFileAgentDemo = lazy(() => import("./pages/demos/SecureFileAgentDemo"));
const SecureFileMftDemo = lazy(() => import("./pages/demos/SecureFileMftDemo"));
const WsGuardianDemo = lazy(() => import("./pages/demos/WsGuardianDemo"));
const SoportePage = lazy(() => import("./pages/SoportePage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PartnersPrograma = lazy(() => import("./pages/PartnersPrograma"));
const PartnerLoginPage = lazy(() => import("./pages/partners/PartnerLoginPage"));
const PartnerPortalPage = lazy(() => import("./pages/partners/PartnerPortalPage"));
const SecureFileResourcesPage = lazy(() => import("./pages/partners/SecureFileResourcesPage"));
const SecureFileBuyerPersonasPage = lazy(() => import("./pages/partners/SecureFileBuyerPersonasPage"));
const SecureFilePlaybookPage = lazy(() => import("./pages/partners/SecureFilePlaybookPage"));
const WsGuardianResourcesPage = lazy(() => import("./pages/partners/WsGuardianResourcesPage"));
const AnkaSecureResourcesPage = lazy(() => import("./pages/partners/AnkaSecureResourcesPage"));
const AnkaSecureChecklistPage = lazy(() => import("./pages/partners/AnkaSecureChecklistPage"));
const ResetPasswordPage = lazy(() => import("./pages/partners/ResetPasswordPage"));
const AnkaSecureProduct = lazy(() => import("./pages/productos/AnkaSecureProduct"));
const QuienesSomosPage = lazy(() => import("./pages/nosotros/QuienesSomosPage"));
const SessionTechPage = lazy(() => import("./pages/nosotros/SessionTechPage"));
const SgcSgsiPage = lazy(() => import("./pages/nosotros/SgcSgsiPage"));
const RsePage = lazy(() => import("./pages/nosotros/RsePage"));
const ReconocimientosPage = lazy(() => import("./pages/nosotros/ReconocimientosPage"));
const CareersPage = lazy(() => import("./pages/nosotros/CareersPage"));
const BlogPage = lazy(() => import("./pages/nosotros/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/nosotros/BlogArticlePage"));

/** /nosotros/blog/:id — a category slug renders the filtered blog listing, anything else is an article */
const BlogEntry = () => {
  const { id } = useParams();
  const category = categoryFromSlug(id);
  return category ? <BlogPage key={category} fixedCategory={category} /> : <BlogArticlePage />;
};
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FabricaSoftwarePage = lazy(() => import("./pages/FabricaSoftwarePage"));
const TransferenciasSeguras = lazy(() => import("./pages/TransferenciasSeguras"));
const MftLandingPage = lazy(() => import("./pages/MftLandingPage"));
const InstanaLandingPage = lazy(() => import("./pages/InstanaLandingPage"));
const FactorySeoLandingPage = lazy(() => import("./pages/FactorySeoLandingPage"));
const PoliticaPrivacidadPage = lazy(() => import("./pages/PoliticaPrivacidadPage"));
const ObservabilidadMonitoreoPage = lazy(() => import("./pages/ObservabilidadMonitoreoPage"));
const CryptoAgilityLandingPage = lazy(() => import("./pages/CryptoAgilityLandingPage"));
const GiftCardCryptoPage = lazy(() => import("./pages/GiftCardCryptoPage"));
const IngenieriaSoftwareIAPage = lazy(() => import("./pages/IngenieriaSoftwareIAPage"));
const DesarrolloSoftwareIaPage = lazy(() => import("./pages/DesarrolloSoftwareIaPage"));
const AutomatizacionITPage = lazy(() => import("./pages/AutomatizacionITPage"));
const SoberaniaCriptograficaPage = lazy(() => import("./pages/SoberaniaCriptograficaPage"));
const FabricaAutomatizacionPage = lazy(() => import("./pages/FabricaAutomatizacionPage"));
const GraciasPorTuRespuestaPage = lazy(() => import("./pages/GraciasPorTuRespuestaPage"));
const EvaluacionRiesgoPqcPage = lazy(() => import("./pages/EvaluacionRiesgoPqcPage"));
const AuditoriaResilienciaPostCuanticaPage = lazy(() => import("./pages/AuditoriaResilienciaPostCuanticaPage"));
const ResilienciaDigital2026Page = lazy(() => import("./pages/ResilienciaDigital2026Page"));
const ContinuidadDigitalObservabilidadPage = lazy(() => import("./pages/ContinuidadDigitalObservabilidadPage"));
const IngenieriaAumentadaIAPage = lazy(() => import("./pages/IngenieriaAumentadaIAPage"));
const ObservabilidadAplicacionesPage = lazy(() => import("./pages/ObservabilidadAplicacionesPage"));
const TransferenciaSeguraArchivosPage = lazy(() => import("./pages/TransferenciaSeguraArchivosPage"));
const AnkaSecurePlanoControlLandingPage = lazy(() => import("./pages/AnkaSecurePlanoControlLandingPage"));
const MigracionCriptografiaPostCuanticaPage = lazy(() => import("./pages/MigracionCriptografiaPostCuanticaPage"));
const AutomatizacionOperacionesTiPage = lazy(() => import("./pages/AutomatizacionOperacionesTiPage"));
const ReduccionRiesgoFraudePage = lazy(() => import("./pages/ReduccionRiesgoFraudePage"));
const CriptoAgilidadSfcPage = lazy(() => import("./pages/CriptoAgilidadSfcPage"));
const WebinarDatosCriticosPage = lazy(() => import("./pages/WebinarDatosCriticosPage"));
const GobernanzaCriptograficaTercerosPage = lazy(() => import("./pages/GobernanzaCriptograficaTercerosPage"));
const GobiernoCriptograficoPage = lazy(() => import("./pages/GobiernoCriptograficoPage"));
const SeguridadTiTransferenciaArchivosMftPage = lazy(() => import("./pages/SeguridadTiTransferenciaArchivosMftPage"));
const DiagnosticoAplicacionesPage = lazy(() => import("./pages/DiagnosticoAplicacionesPage"));
const ConsultoriaCriptoagilidadPqcPage = lazy(() => import("./pages/ConsultoriaCriptoagilidadPqcPage"));
const AssessmentCriptograficoActivoPage = lazy(() => import("./pages/AssessmentCriptograficoActivoPage"));
const IntercambioSoberanoTercerosPage = lazy(() => import("./pages/IntercambioSoberanoTercerosPage"));
const ObservabilidadIbmInstanaPage = lazy(() => import("./pages/ObservabilidadIbmInstanaPage"));
const ChatBot = lazy(() => import("./components/ChatBot"));
import { AdminRoute } from "./components/admin/AdminRoute";
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminPartnersPage = lazy(() => import("./pages/admin/AdminPartnersPage"));
const AdminOpportunitiesPage = lazy(() => import("./pages/admin/AdminOpportunitiesPage"));
const AdminTeamPage = lazy(() => import("./pages/admin/AdminTeamPage"));

const queryClient = new QueryClient();

export const ThemeContext = createContext<{
  theme: "light" | "dark";
  toggleTheme: () => void;
}>({ theme: "light", toggleTheme: () => {} });

const App = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <GaRouteTracker />
              <RouteErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/fabrica-software" element={<FabricaSoftwarePage />} />
                <Route path="/transferencias-seguras" element={<TransferenciasSeguras />} />
                <Route path="/mft-transferencia-gestionada-archivos" element={<MftLandingPage />} />
                <Route path="/ibm-instana-observabilidad-itac" element={<InstanaLandingPage />} />
                <Route path="/aceleracion-desarrollo-software" element={<FactorySeoLandingPage />} />
                <Route path="/politica-privacidad" element={<PoliticaPrivacidadPage />} />
                <Route path="/observabilidad-monitoreo" element={<ObservabilidadMonitoreoPage />} />
                <Route path="/agilidad-criptografica-modernizacion-ti" element={<CryptoAgilityLandingPage />} />
                <Route path="/evaluacion-empresarial-criptoagilidad" element={<GiftCardCryptoPage />} />
                <Route path="/ingenieria-software-ia-supervisada" element={<IngenieriaSoftwareIAPage />} />
                <Route path="/desarrollo-software-ia" element={<DesarrolloSoftwareIaPage />} />
                <Route path="/automatizacion-it-infraestructura-colombia" element={<AutomatizacionITPage />} />
                <Route path="/resiliencia-y-cumplimiento-criptografico-post-cuantico" element={<SoberaniaCriptograficaPage />} />
                <Route path="/fabrica-automatizacion" element={<FabricaAutomatizacionPage />} />
                <Route path="/gracias-por-tu-respuesta" element={<GraciasPorTuRespuestaPage />} />
                <Route path="/evaluacion-riesgo-pqc" element={<EvaluacionRiesgoPqcPage />} />
                <Route path="/auditoria-resiliencia-post-cuantica" element={<AuditoriaResilienciaPostCuanticaPage />} />
                <Route path="/evaluacion-observabilidad" element={<ResilienciaDigital2026Page />} />
                <Route path="/resiliencia-digital-2026" element={<ResilienciaDigital2026Page />} />
                <Route path="/continuidad-digital-observabilidad" element={<ContinuidadDigitalObservabilidadPage />} />
                <Route path="/ingenieria-aumentada-ia" element={<IngenieriaAumentadaIAPage />} />
                <Route path="/observabilidad-aplicaciones-instana" element={<ObservabilidadAplicacionesPage />} />
                <Route path="/transferencia-segura-archivos" element={<TransferenciaSeguraArchivosPage />} />
                <Route path="/plano-control-criptografico-ankasecure" element={<AnkaSecurePlanoControlLandingPage />} />
                <Route path="/migracion-criptografia-post-cuantica" element={<MigracionCriptografiaPostCuanticaPage />} />
                <Route path="/cripto-agilidad-sfc" element={<CriptoAgilidadSfcPage />} />
                <Route path="/automatizacion-operaciones-ti" element={<AutomatizacionOperacionesTiPage />} />
                <Route path="/webinar-datos-criticos" element={<WebinarDatosCriticosPage />} />
                <Route path="/reduccion-riesgo-fraude" element={<ReduccionRiesgoFraudePage />} />
                <Route path="/gobernanza-criptografica-terceros" element={<GobernanzaCriptograficaTercerosPage />} />
                <Route path="/journey-criptografico" element={<GobiernoCriptograficoPage />} />
                <Route path="/gobierno-criptografico" element={<Navigate to="/journey-criptografico" replace />} />
                <Route path="/seguridad-ti-transferencia-archivos-mft" element={<SeguridadTiTransferenciaArchivosMftPage />} />
                <Route path="/servicio-de-diagnostico-de-aplicaciones" element={<DiagnosticoAplicacionesPage />} />
                <Route path="/assessment-pasivo-criptografico" element={<ConsultoriaCriptoagilidadPqcPage />} />
                <Route path="/soberania-criptografica-intercambio-terceros" element={<IntercambioSoberanoTercerosPage />} />
                <Route path="/intercambio-con-terceros" element={<Navigate to="/soberania-criptografica-intercambio-terceros" replace />} />
                <Route path="/observabilidad-ibm-instana" element={<ObservabilidadIbmInstanaPage />} />
                <Route path="/assessment-activo-criptografico" element={<AssessmentCriptograficoActivoPage />} />
                <Route path="/consultoria-criptoagilidad-transicion-pqc" element={<Navigate to="/assessment-pasivo-criptografico" replace />} />
                <Route path="/assessment-criptografico-activo" element={<Navigate to="/assessment-activo-criptografico" replace />} />

                {/* Product Pages */}
                <Route path="/productos/securefile-agent" element={<SecureFileAgentProduct />} />
                <Route path="/productos/securefile-mft" element={<SecureFileMftProduct />} />
                <Route path="/productos/ws-guardian" element={<WsGuardianProduct />} />
                <Route path="/productos/ankasecure" element={<AnkaSecureProduct />} />
                {/* Demos */}
                <Route path="/demos/securefile-agent" element={<SecureFileAgentDemo />} />
                <Route path="/demos/securefile-mft" element={<SecureFileMftDemo />} />
                <Route path="/demos/ws-guardian" element={<WsGuardianDemo />} />
                {/* About Us */}
                <Route path="/nosotros/quienes-somos" element={<QuienesSomosPage />} />
                <Route path="/nosotros/sessiontech" element={<SessionTechPage />} />
                <Route path="/nosotros/sgc-sgsi" element={<SgcSgsiPage />} />
                <Route path="/nosotros/rse" element={<RsePage />} />
                <Route path="/nosotros/reconocimientos" element={<ReconocimientosPage />} />
                <Route path="/nosotros/trabaja-con-nosotros" element={<CareersPage />} />
                <Route path="/nosotros/blog" element={<BlogPage />} />
                <Route path="/nosotros/blog/:id" element={<BlogEntry />} />
                <Route path="/contacto" element={<ContactPage />} />
                {/* Other Pages */}
                <Route path="/soporte" element={<SoportePage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/partners/programa" element={<PartnersPrograma />} />
                <Route path="/partners/login" element={<PartnerLoginPage />} />
                <Route path="/partners/portal" element={<PartnerPortalPage />} />
                <Route path="/partners/portal/recursos/securefile" element={<PartnerGuard><SecureFileResourcesPage /></PartnerGuard>} />
                <Route path="/partners/portal/recursos/securefile/playbook" element={<PartnerGuard><SecureFilePlaybookPage /></PartnerGuard>} />
                <Route path="/partners/portal/recursos/securefile/buyer-personas" element={<PartnerGuard><SecureFileBuyerPersonasPage /></PartnerGuard>} />
                <Route path="/partners/portal/recursos/ws-guardian" element={<PartnerGuard><WsGuardianResourcesPage /></PartnerGuard>} />
                <Route path="/partners/portal/recursos/ankasecure" element={<PartnerGuard><AnkaSecureResourcesPage /></PartnerGuard>} />
                <Route path="/partners/portal/recursos/ankasecure/checklist" element={<PartnerGuard><AnkaSecureChecklistPage /></PartnerGuard>} />
                <Route path="/partners/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/partners/admin"
                  element={
                    <AdminRoute>
                      <AdminLayout />
                    </AdminRoute>
                  }
                >
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="partners" element={<AdminPartnersPage />} />
                  <Route path="oportunidades" element={<AdminOpportunitiesPage />} />
                  <Route
                    path="equipo"
                    element={
                      <AdminRoute requireAdmin>
                        <AdminTeamPage />
                      </AdminRoute>
                    }
                  />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </RouteErrorBoundary>
              <Suspense fallback={null}>
                <ChatBot />
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ThemeContext.Provider>
  );
};

export default App;
